/* Northtac theme — global.js */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container) {
  // Query focusable elements at keydown time — drawer contents get re-rendered
  // (e.g. the cart drawer after an add-to-cart), which would orphan cached refs.
  container.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first && first.focus();
    }
    if ((event.target === first || event.target === container) && event.shiftKey) {
      event.preventDefault();
      last && last.focus();
    }
  });
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  };
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* Shared serial queue for ALL cart mutations (add / change / update).
   Shopify processes one cart mutation at a time; parallel requests race:
   409/429 rejections and out-of-order section renders. Every cart fetch in
   the theme goes through here so requests run strictly one after another. */
window.cartQueue = window.cartQueue || Promise.resolve();
function enqueueCartRequest(task) {
  const run = window.cartQueue.then(() => task());
  window.cartQueue = run.then(
    () => undefined,
    () => undefined // a failed task must not poison the chain
  );
  return run;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* Cart fetches MUST be time-capped: on a flaky connection a fetch can hang
   for minutes, and since all cart mutations share one serial queue, a single
   hung request would make every cart control on the page look dead. */
function fetchWithTimeout(url, opts, ms) {
  if (typeof AbortController === 'undefined') return fetch(url, opts);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, { ...opts, signal: controller.signal }).finally(() => clearTimeout(timer));
}

/* ---------- Header ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header') || this;
    this.wrapper = this.closest('.shopify-section');
    this.transparent = this.dataset.transparent === 'true';
    this.sticky = this.dataset.sticky !== 'false';

    if (this.wrapper) {
      this.wrapper.classList.add('section-header-wrapper');
      if (this.sticky) this.wrapper.classList.add('section-header-sticky');
      if (this.transparent) this.wrapper.classList.add('shopify-section--header-transparent');
    }

    this.setHeight();
    window.addEventListener('resize', debounce(() => this.setHeight(), 150));
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    // Solid header while any dropdown/mega menu or search is open
    this.addEventListener('toggle', () => this.refreshSolid(), true);
    this.addEventListener('mouseenter', () => this.refreshSolid());
    this.addEventListener('mouseleave', () => this.refreshSolid());
  }

  setHeight() {
    // Use the precise fractional height: offsetHeight rounds to an integer,
    // which leaves a sub-pixel gap under the transparent-header negative margin.
    const h = this.header.getBoundingClientRect().height;
    if (h > 0) document.documentElement.style.setProperty('--header-height', `${h.toFixed(2)}px`);
  }

  hasOpenPanel() {
    return !!this.querySelector('details[open], .search-modal.active');
  }

  refreshSolid() {
    if (!this.transparent) return;
    const solid = window.scrollY > 12 || this.hasOpenPanel() || this.matches(':hover');
    this.header.classList.toggle('header--solid', solid);
  }

  onScroll() {
    if (this.transparent) this.refreshSolid();
  }
}
customElements.define('sticky-header', StickyHeader);

/* Desktop dropdown / mega menu: open on hover with close delay, close on
   outside click + Escape. Open/close are animated in BOTH directions: the
   [open] attribute mounts the panel, the .is-shown class drives a transition,
   and on close the attribute is removed only after the transition finishes. */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.details = Array.from(this.querySelectorAll('details[data-hover-menu]'));
    this.details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      detail.addEventListener('mouseenter', () => {
        clearTimeout(detail._closeTimer);
        this.closeAll(detail);
        this.open(detail);
      });
      detail.addEventListener('mouseleave', () => {
        detail._closeTimer = setTimeout(() => this.close(detail), 160);
      });
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (detail.classList.contains('is-shown')) {
          // second click/tap: follow the link if the item has one, else close
          if (summary.dataset.href) window.location.href = summary.dataset.href;
          else this.close(detail);
        } else {
          this.closeAll(detail);
          this.open(detail);
        }
      });
      detail.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') {
          this.close(detail);
          summary.focus();
        }
      });
      detail.addEventListener('focusout', (event) => {
        if (!detail.contains(event.relatedTarget)) this.close(detail);
      });
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAll();
    });
  }

  open(detail) {
    clearTimeout(detail._closeTimer);
    clearTimeout(detail._removeTimer);
    if (!detail.hasAttribute('open')) detail.setAttribute('open', '');
    // double rAF: let the browser paint the mounted (hidden) state first,
    // so the transition to .is-shown actually animates
    requestAnimationFrame(() => requestAnimationFrame(() => detail.classList.add('is-shown')));
  }

  close(detail) {
    if (!detail.hasAttribute('open')) return;
    detail.classList.remove('is-shown');
    clearTimeout(detail._removeTimer);
    detail._removeTimer = setTimeout(() => detail.removeAttribute('open'), 220);
  }

  closeAll(except) {
    this.details.forEach((d) => {
      if (d !== except) this.close(d);
    });
  }
}
customElements.define('header-menu', HeaderMenu);

/* ---------- Drawers ---------- */
class DrawerComponent extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.drawer__overlay');
    this.panel = this.querySelector('.drawer__panel');
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    // Delegated: close buttons inside re-rendered content (cart drawer refresh)
    // keep working because the listener lives on the drawer root, not the button.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
    trapFocus(this);
  }

  open(opener) {
    this.opener = opener;
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    const focusTarget = this.querySelector('[data-drawer-focus]') || this.querySelector('.drawer__close');
    setTimeout(() => focusTarget && focusTarget.focus(), 120);
    this.dispatchEvent(new CustomEvent('drawer:open'));
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (this.opener) this.opener.focus();
    this.dispatchEvent(new CustomEvent('drawer:close'));
  }
}
customElements.define('drawer-component', DrawerComponent);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-target]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerTarget);
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open(trigger);
  }
});

/* ---------- Quantity input ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.plusButton = this.querySelector('button[name="plus"]');
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const prev = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        else this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncMax();
      })
    );
    this.input.addEventListener('change', () => this.syncMax());
    this.syncMax();
  }

  /* Grey out + when the value has reached the stock cap (max attribute).
     Re-run whenever the value OR the max changes (product.js calls this
     after a variant switch updates max). */
  syncMax() {
    if (!this.plusButton || !this.input) return;
    const max = parseInt(this.input.max, 10);
    const atMax = !isNaN(max) && max >= 0 && parseInt(this.input.value, 10) >= max;
    this.plusButton.toggleAttribute('disabled', atMax);
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Cart drawer ---------- */
class CartDrawer extends DrawerComponent {
  renderContents(parsedHTML) {
    const newInner = parsedHTML.querySelector('#CartDrawer-Inner');
    const currentInner = this.querySelector('#CartDrawer-Inner');
    if (newInner && currentInner) currentInner.replaceWith(newInner);
    CartDrawer.updateCartCount(this);
  }

  static updateCartCount(scope) {
    const source = (scope || document).querySelector('[data-cart-count-source]');
    if (!source) return;
    const count = source.dataset.cartCountSource;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const changed = el.textContent !== count;
      el.textContent = count;
      el.classList.toggle('visually-hidden', count === '0');
      if (changed && count !== '0') {
        // restart the bump animation even on rapid successive updates
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Product form (AJAX add to cart) ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    const idInput = this.form.querySelector('[name=id]');
    if (idInput) idInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
    this.errorWrapper = this.querySelector('.product-form__error');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButtons.some((b) => b.getAttribute('aria-disabled') === 'true')) return;

    // Shared add-lock across ALL forms for the same variant (main buy button,
    // sticky ATC, drawer upsells): each form self-locks, but without a shared
    // lock rapid alternating clicks queue the same add twice.
    const idInput = this.form.querySelector('[name=id]');
    const variantId = idInput ? String(idInput.value) : null;
    window.pendingAddVariants = window.pendingAddVariants || new Set();
    if (variantId && window.pendingAddVariants.has(variantId)) return;
    if (variantId) window.pendingAddVariants.add(variantId);

    this.handleErrorMessage();
    this.submitButtons.forEach((b) => {
      b.setAttribute('aria-disabled', 'true');
      b.classList.add('button--loading');
    });

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    // Item count BEFORE the add — used to reconcile ambiguous failures
    const countEl = document.querySelector('[data-cart-count]');
    const preCount = countEl ? parseInt(countEl.textContent, 10) : null;

    /* Stock clamp: asking for more than the remaining stock (inventory minus
       what the cart already holds) gets capped to the maximum that CAN be
       added, instead of letting Shopify reject the whole add. Applies only
       to tracked, no-oversell variants with known data (PDP forms). The
       in-cart count comes from the drawer DOM; ONLY when that view would
       clamp or block do we confirm against /cart.js first — the DOM can be
       stale if the cart changed in another tab, and a stale overcount must
       never block a legitimate add. */
    let clampedTo = 0;
    const vdata = this.findVariantData(variantId);
    const requested = parseInt(formData.get('quantity') || '1', 10) || 1;

    const abort = (message) => {
      if (variantId) window.pendingAddVariants.delete(variantId);
      this.submitButtons.forEach((b) => {
        b.removeAttribute('aria-disabled');
        b.classList.remove('button--loading');
      });
      this.handleErrorMessage(message);
    };

    const decideClamp = () => {
      if (!vdata || !vdata.inventory_management || vdata.inventory_policy !== 'deny') {
        return Promise.resolve(true);
      }
      const inventory = vdata.inventory_quantity || 0;
      if (requested <= inventory - this.quantityInCart(variantId)) return Promise.resolve(true);
      // would clamp/block — confirm the in-cart count against the server
      return fetchWithTimeout('/cart.js', {}, 8000)
        .then((r) => r.json())
        .then((cart) =>
          cart.items
            .filter((i) => String(i.variant_id) === String(variantId))
            .reduce((sum, i) => sum + i.quantity, 0)
        )
        .catch(() => this.quantityInCart(variantId)) // offline: fall back to the DOM view
        .then((inCart) => {
          const remaining = inventory - inCart;
          if (remaining <= 0) {
            abort(window.cartStrings.stockAllInCart || window.cartStrings.error);
            return false;
          }
          if (requested > remaining) {
            formData.set('quantity', String(remaining));
            clampedTo = remaining;
          }
          return true;
        });
    };

    // Timeout but NO auto-retry on AMBIGUOUS failures: re-sending an add is
    // not idempotent — a retry could add the item twice. A 429 is different:
    // it means the request was REJECTED before processing, so one delayed
    // retry (after the throttle window) is safe and unambiguous.
    const doAdd = () => fetchWithTimeout(`${window.routes.cart_add_url}`, config, 10000);
    decideClamp().then((go) => {
      if (!go) return;
      enqueueCartRequest(() =>
      doAdd().then((response) => {
        if (response.status === 429) {
          const retryAfterMs = parseFloat(response.headers.get('Retry-After')) * 1000;
          const wait = Math.max(isNaN(retryAfterMs) ? 0 : retryAfterMs, 2500);
          return delay(wait)
            .then(doAdd)
            .then((retry) => {
              if (retry.status === 429) throw new Error('cart add: throttled');
              return retry.json().catch(() => {
                throw new Error('cart add: non-JSON response');
              });
            });
        }
        return response.json().catch(() => {
          throw new Error('cart add: non-JSON response'); // proxy/upstream error page
        });
      })
    )
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        if (window.themeSettings.cartType === 'page' || !cartDrawer) {
          window.location = window.routes.cart_url;
          return;
        }
        if (response.sections && response.sections['cart-drawer']) {
          const html = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');
          cartDrawer.renderContents(html);
        }
        cartDrawer.open(this.submitButtons[0]);
        if (clampedTo > 0) this.showCartNotice((window.cartStrings.stockClamped || '').replace('[count]', clampedTo));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(() => this.reconcileAdd(preCount, cartDrawer))
      .finally(() => {
        if (variantId) window.pendingAddVariants.delete(variantId);
        this.submitButtons.forEach((b) => {
          b.removeAttribute('aria-disabled');
          b.classList.remove('button--loading');
        });
      });
    });
  }

  /* An ambiguous add failure (timeout, non-JSON upstream error page) does
     NOT mean the add failed. A blind retry could add the item twice; a blind
     error message scares the shopper off an add that actually landed.
     Reconcile instead: read the real cart — if the count grew, treat it as
     success and refresh the drawer; only otherwise show the error. */
  reconcileAdd(preCount, cartDrawer) {
    return fetchWithTimeout('/cart.js', {}, 8000)
      .then((r) => r.json())
      .then((cart) => {
        const landed = preCount !== null && !isNaN(preCount) && cart.item_count > preCount;
        if (!landed) {
          this.handleErrorMessage(window.cartStrings.error);
          return;
        }
        if (!cartDrawer || window.themeSettings.cartType === 'page') {
          window.location = window.routes.cart_url;
          return;
        }
        return fetchWithTimeout(`${window.location.pathname}?sections=cart-drawer`, {}, 8000)
          .then((r) => r.json())
          .then((sections) => {
            if (sections && sections['cart-drawer']) {
              const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
              cartDrawer.renderContents(html);
            }
            cartDrawer.open(this.submitButtons[0]);
            document.dispatchEvent(new CustomEvent('cart:updated'));
          });
      })
      .catch(() => this.handleErrorMessage(window.cartStrings.error));
  }

  handleErrorMessage(errorMessage = false) {
    if (!this.errorWrapper) {
      // No inline error area (sticky ATC, drawer upsells): use the drawer's
      // banner instead of alert() — alerts block the page (and automation).
      if (errorMessage) this.showCartNotice(errorMessage);
      return;
    }
    this.errorWrapper.toggleAttribute('hidden', !errorMessage);
    this.errorWrapper.textContent = errorMessage || '';
  }

  /* Message in the cart drawer's banner (survives until timeout). Used for
     the stock-clamp note and as the fallback error surface. */
  showCartNotice(message) {
    if (!message) return;
    const el = document.querySelector('cart-drawer .cart-errors') || document.querySelector('.cart-errors');
    if (!el) return;
    el.textContent = message;
    el.removeAttribute('hidden');
    clearTimeout(el._noticeTimer);
    el._noticeTimer = setTimeout(() => el.setAttribute('hidden', ''), 5000);
  }

  /* Variant inventory data from any ProductJSON block on the page (PDP main
     section). Returns null for contexts without data (drawer upsells). */
  findVariantData(variantId) {
    if (!variantId) return null;
    const scripts = document.querySelectorAll('script[id^="ProductJSON-"]');
    for (let i = 0; i < scripts.length; i++) {
      try {
        const data = JSON.parse(scripts[i].textContent);
        const v = (data.variants || []).find((x) => String(x.id) === String(variantId));
        if (v) return v;
      } catch (e) {
        /* malformed block — skip */
      }
    }
    return null;
  }

  /* How many units of this variant the cart already holds (line keys are
     "variantId:hash"; a variant can span multiple lines with properties). */
  quantityInCart(variantId) {
    let total = 0;
    document.querySelectorAll(`cart-items .cart-item[data-key^="${variantId}:"] .quantity__input`).forEach((input) => {
      total += parseInt(input.value, 10) || 0;
    });
    return total;
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Search modal + predictive search ---------- */
class SearchModal extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.search-modal');
    this.input = this.querySelector('input[type="search"]');
    this.resultsBox = this.querySelector('[data-predictive-results]');
    this.toggleButtons = document.querySelectorAll('[data-search-toggle]');
    this.toggleButtons.forEach((btn) =>
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.panel.classList.contains('active') ? this.close() : this.open();
      })
    );
    document.addEventListener('click', (event) => {
      if (this.panel.classList.contains('active') && !this.contains(event.target) && !event.target.closest('[data-search-toggle]')) {
        this.close();
      }
    });
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
    if (this.input && window.themeSettings.predictiveSearch) {
      this.input.addEventListener('input', debounce(() => this.getResults(), 250));
    }
  }

  open() {
    this.panel.classList.add('active');
    setTimeout(() => this.input && this.input.focus(), 100);
  }

  close() {
    this.panel.classList.remove('active');
  }

  getResults() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      if (this.resultsBox) this.resultsBox.innerHTML = '';
      return;
    }
    fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (this.resultsBox && resultsMarkup) this.resultsBox.innerHTML = resultsMarkup.innerHTML;
      })
      .catch(() => {});
  }
}
customElements.define('search-modal', SearchModal);

/* ---------- Slideshow (hero) ---------- */
class SlideshowComponent extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero__dot'));
    this.counter = this.querySelector('[data-slide-counter]');
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = (parseInt(this.dataset.interval, 10) || 6) * 1000;
    this.index = 0;
    if (this.slides.length <= 1) return;

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));
    if (this.autoplay) {
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
    }
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.goTo((this.index + 1) % this.slides.length), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
  }

  goTo(i, manual = false) {
    this.slides[this.index].classList.remove('is-active');
    if (this.dots[this.index]) this.dots[this.index].classList.remove('is-active');
    this.index = i;
    this.slides[this.index].classList.add('is-active');
    if (this.dots[this.index]) this.dots[this.index].classList.add('is-active');
    if (this.counter) this.counter.textContent = `${String(i + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    const video = this.slides[this.index].querySelector('video');
    if (video) video.play().catch(() => {});
    if (manual && this.autoplay) this.start();
  }
}
customElements.define('slideshow-component', SlideshowComponent);

/* ---------- Announcement bar rotation ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
    if (this.items.length <= 1) return;
    this.mode = this.dataset.rotate || 'mobile'; // 'mobile' | 'always'
    const seconds = parseInt(this.dataset.interval, 10);
    const interval = (seconds >= 2 && seconds <= 30 ? seconds : 4) * 1000;
    this.index = 0;
    this.items[0].classList.add('is-active');
    setInterval(() => {
      if (this.mode !== 'always' && window.innerWidth >= 750) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }, interval);
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- Reveal on scroll ---------- */
function initReveal() {
  if (!window.themeSettings.revealOnScroll) return;
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el));
}

/* ---------- FAQ scrollspy ---------- */
function initFaqNav() {
  const nav = document.querySelector('[data-faq-nav]');
  if (!nav) return;
  const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
  const groups = links
    .map((link) => document.querySelector(link.getAttribute('href')))
    .filter(Boolean);
  if (!groups.length) return;
  const spy = () => {
    let current = groups[0];
    groups.forEach((g) => {
      if (g.getBoundingClientRect().top < window.innerHeight * 0.3) current = g;
    });
    links.forEach((link) => link.classList.toggle('is-active', link.getAttribute('href') === `#${current.id}`));
  };
  window.addEventListener('scroll', debounce(spy, 80), { passive: true });
  spy();
}

document.addEventListener('DOMContentLoaded', () => {
  initReveal();
  initFaqNav();
});

document.addEventListener('shopify:section:load', () => {
  initReveal();
});
